import { motion } from 'motion/react';
import { Card } from './ui/card';
import { Droplets } from 'lucide-react';

interface RainGaugeProps {
  current: number;
  today: number;
  week: number;
}

export function RainGauge({ current, today, week }: RainGaugeProps) {
  const maxHeight = 200;
  const currentHeight = Math.min((current / 50) * maxHeight, maxHeight);
  const todayPercentage = Math.min((today / 50) * 100, 100);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="p-6">
        <h3 className="mb-4">Opady</h3>
        
        <div className="flex items-end justify-around gap-8">
          {/* Rain gauge visualization */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-20 h-48 bg-gray-100 rounded-full overflow-hidden border-2 border-gray-300">
              <motion.div
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-300"
                initial={{ height: 0 }}
                animate={{ height: currentHeight }}
                transition={{ duration: 2, ease: 'easeOut' }}
              />
              
              {/* Water drops animation */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-blue-400 rounded-full"
                  initial={{ top: -10, left: `${20 + i * 15}%`, opacity: 0 }}
                  animate={{
                    top: 200,
                    opacity: [0, 1, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: 'linear',
                  }}
                />
              ))}
              
              {/* Scale marks */}
              {[0, 25, 50].map((mark) => (
                <div
                  key={mark}
                  className="absolute left-0 right-0 border-t border-gray-300"
                  style={{ bottom: `${(mark / 50) * 100}%` }}
                />
              ))}
            </div>
            
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Droplets className="w-8 h-8 text-blue-500" />
            </motion.div>
          </div>
          
          {/* Statistics */}
          <div className="flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="text-sm text-gray-500 block">Aktualnie</span>
              <span className="text-2xl">{current.toFixed(2)} mm</span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <span className="text-sm text-gray-500 block">Dziś</span>
              <span className="text-2xl">{today.toFixed(2)} mm</span>
              <div className="w-32 h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <motion.div
                  className="h-full bg-blue-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${todayPercentage}%` }}
                  transition={{ duration: 1.5, delay: 0.5 }}
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <span className="text-sm text-gray-500 block">Tydzień</span>
              <span className="text-2xl">{week.toFixed(2)} mm</span>
            </motion.div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
